import { useMemo } from 'react';
import { LoaderIcon } from 'react-hot-toast';
import { useContractQuery } from './contract.generated';
import { t } from 'i18next';
import { fDate, useParameters } from '@web/utils/src';
import { ContractType } from '@/types';
import Attachment from './components/ContractAttachment';
import SingleDetail from './components/ContractDetail';
import LineItem from './components/ContractLineItem';

type RouteParams = {
  retailerId: string;
  contractId: string;
};

const ContractDetailsPage: React.FC = () => {
  const { retailerId, contractId } = useParameters<RouteParams>();

  const [{ data, fetching }] = useContractQuery({
    variables: {
      contractId,
    },
  });

  const contract = data?.currentUser?.contract;

  const expectedDeliveryValue = useMemo(() => {
    const start = contract?.expectedDeliveryAt?.start;
    const end = contract?.expectedDeliveryAt?.end;
    return `${fDate(start)} - ${fDate(end)}`;
  }, [contract]);

  if (!contract) return <LoaderIcon className="mx-auto mt-10-0" />;

  return (
    <>
      {contract.attachment && (
        <Attachment
          attachment={contract.attachment}
          contractId={contract.erpID!}
        />
      )}

      <h2 className="contract-type">
        {contract.type === ContractType.Purchase
          ? t('contracts.purchase_contract')
          : t('contracts.sell_contract')}
      </h2>
      <SingleDetail
        label={t('contracts.contract_number')}
        value={contract.erpID || ''}
      />
      <SingleDetail
        label={t('contracts.retailer')}
        value={contract.retailer.name || ''}
      />
      <SingleDetail
        label={t('contracts.date')}
        value={fDate(contract.createdAt)}
      />
      <SingleDetail
        label={t('contracts.parity')}
        value={contract.parityDeliveryMethod || ''}
      />
      <SingleDetail
        label={t('contracts.expectedDeliveryDates')}
        value={expectedDeliveryValue}
      />
      <SingleDetail
        label={t('contracts.harvest_year')}
        value={contract.harvestYear || '-'}
      />
      <SingleDetail
        label={t('contracts.price_details')}
        value={contract.priceDetails || ''}
      />

      <div className="grid grid-cols-3 gap-4-0 my-4-0">
        {contract.lineItems.map((lineItem, i) => (
          <LineItem
            key={lineItem.erpArticleID}
            lineItem={lineItem}
            contractType={contract.type}
            index={i}
          />
        ))}
      </div>
    </>
  );
};

export default ContractDetailsPage;
